import { useState } from "react";
import { Menu, LogOut, User } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { Logo } from "../Logo";
import { AdminNavLinks } from "./AdminNavLinks";
import { Sheet, SheetContent, SheetTitle } from "../ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { NotificationBell } from "../NotificationBell";

const ROLE_LABEL = { super_admin: "Super Admin", admin: "Admin" };

export const AdminTopBar = ({ title }) => {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="sticky top-0 z-30 bg-clean-white border-b border-soft-gray" data-testid="admin-topbar">
      <div className="flex items-center justify-between px-4 sm:px-8 h-16">
        <div className="flex items-center gap-3 min-w-0">
          <button className="md:hidden text-midnight-black" data-testid="admin-mobile-menu-toggle" onClick={() => setMobileOpen(true)} aria-label="Open navigation">
            <Menu className="w-5 h-5" />
          </button>
          <div>
            <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-midnight-black/40" data-testid="admin-breadcrumb">
              Admin Panel
            </p>
            <h1 className="font-extrabold uppercase text-sm sm:text-base tracking-tight truncate" data-testid="admin-page-title">
              {title}
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-4">
          <NotificationBell />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 pl-1 pr-2 sm:pr-3 py-1 rounded-full border border-soft-gray hover:border-stranger-orange transition-colors" data-testid="admin-profile-menu-trigger">
                <span className="w-7 h-7 rounded-full bg-midnight-black text-clean-white flex items-center justify-center">
                  <User className="w-3.5 h-3.5" />
                </span>
                <span className="hidden sm:flex flex-col items-start leading-tight">
                  <span className="text-xs font-bold" data-testid="admin-current-name">
                    {user?.first_name} {user?.last_name}
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-wide text-stranger-orange" data-testid="admin-current-role">
                    {ROLE_LABEL[user?.role] || user?.role}
                  </span>
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" data-testid="admin-profile-menu-content">
              <DropdownMenuLabel>
                {user?.first_name} {user?.last_name}
                <p className="text-xs font-normal text-midnight-black/50 mt-0.5">{user?.email}</p>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="admin-logout-menu-item" onClick={logout} className="text-red-600 cursor-pointer">
                <LogOut className="w-3.5 h-3.5 mr-2" /> Log Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="bg-midnight-black border-none p-0 w-72" data-testid="admin-mobile-sidebar">
          <SheetTitle className="sr-only">Admin Navigation</SheetTitle>
          <div className="px-5 py-6 border-b border-white/10">
            <div className="bg-white/5 rounded-lg px-3 py-2 inline-block">
              <Logo className="h-7" />
            </div>
          </div>
          <AdminNavLinks onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>
    </header>
  );
};
